import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Country } from 'src/app/shared/models/dtos/responses/country/country';
import { TranslationService } from 'src/app/core/services/translation/translation.service';
import { UserService } from 'src/app/core/api/user/user.service';
import { SnackBarService } from 'src/app/core/services/snackbar/snackbar.service';
import { specialCharValidator } from 'src/app/shared/validators/registration-validators';
import { MessageRejectedException } from 'src/app/shared/errors/cognito/cognito-errors';
import { BusinessFrozenException } from 'src/app/shared/errors/user-management-service/user-management-service-errors';

type LevelItem = {
  id: number;
  name: string;
};

@Component({
  selector: 'app-modal',
  templateUrl: './update-vip-modal.component.html',
  styleUrls: ['./update-vip-modal.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
/**
 * Modal for business editting
 */
export class UpdateVipModalComponent implements OnInit {
  formGroup = new FormGroup({
    level: new FormControl<LevelItem | null>(null, Validators.required),
    season: new FormControl<string | null>(null, Validators.required),
    reseller: new FormControl<string | null>(null, [
      Validators.maxLength(80),
      specialCharValidator(),
    ]),
  });
  options: LevelItem[] = [];

  currentSeasonOptions: LevelItem[] = [
    {
      id: 0,
      name: 'Entry level',
    },
  ];

  nextSeasonOptions: LevelItem[] = [
    {
      id: 0,
      name: 'Entry level',
    },
  ];

  countries!: Country[];
  businessTypes!: string[];
  originalData: any;
  snackBarLabels = {
    success: 'vip-level-updated',
    error: 'error-vip-level-update',
    emailError: 'email-error-vip-level-update',
    businessFrozen: 'business-is-frozen-exception',
  };
  loading: boolean = false;
  today = new Date().toISOString();
  nextSeasonStartingDate = '';

  JANUARY = 0;
  JULY = 6;
  FIRST = 1;

  /**
   * @constructor
   */
  constructor(
    public dialogRef: MatDialogRef<UpdateVipModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translationService: TranslationService,
    private userService: UserService,
    private snackBarService: SnackBarService
  ) {}

  /**
   * On init
   */
  ngOnInit(): void {
    this.setupVipConfigsAndOptions();

    this.translationService.translateObject(this.snackBarLabels);
    this.today = this.translationService.localizeDate(this.today);
    this.nextSeasonStartingDate = this.translationService.localizeDate(
      this.getNextSeasonDate()
    );
  }

  /**
   * Setup VIP configs and options
   */
  setupVipConfigsAndOptions() {
    this.userService
      .getVipRegion(this.data.businessSummary.region?.id!)
      .subscribe((response) => {
        response.current
          .filter(
            (level) => level.businessLevel.familyPk === this.data.family.id
          )
          .forEach((level) => {
            this.currentSeasonOptions.push({
              id: level.businessLevel.level,
              name: level.name,
            });
          });

        response.next
          .filter(
            (level) => level.businessLevel.familyPk === this.data.family.id
          )
          .forEach((level) => {
            if (!level.businessLevel.markedForDeletion) {
              this.nextSeasonOptions.push({
                id: level.businessLevel.level,
                name: level.name,
              });
            }
          });
      });
  }

  /**
   * Action to send data and close the dialog
   */
  onSuccessClick() {
    this.loading = true;

    const request = {
      businessLevel: this.formGroup.value.level?.id!,
      resellerName: this.formGroup.value.reseller!,
      season: parseInt(this.formGroup.value.season!),
      familyId: this.data.family.id,
    };

    this.userService
      .updateBusinessLevel(this.data.businessSummary.id, request)
      .subscribe({
        next: (response) => {
          this.dialogRef.close({ request, response });
        },
        error: (err) => {
          this.loading = false;
          if (err instanceof MessageRejectedException) {
            this.snackBarService.error(this.snackBarLabels.emailError);
          } else if (err instanceof BusinessFrozenException) {
            return this.snackBarService.error(
              this.snackBarLabels.businessFrozen
            );
          } else {
            this.snackBarService.error(this.snackBarLabels.error);
          }
        },
        complete: () => {
          this.loading = false;
          this.snackBarService.success(this.snackBarLabels.success);
        },
      });
  }

  /**
   * Action to close the dialog
   */
  onFailClick() {
    this.dialogRef.close();
  }

  /**
   * @param {string} controlName
   * @return {FormControl}
   */
  getControl(controlName: string): FormControl {
    return this.formGroup.get(controlName) as FormControl;
  }

  /**
   * @param {any} _event event of the radio button
   */
  onRadioButtonChange(_event: any) {
    const season = this.formGroup.get('season')!.value;
    if (season === '1') {
      this.options = this.currentSeasonOptions;
    } else {
      this.options = this.nextSeasonOptions;
    }
  }

  /**
   * @param {BusinessLevel} businessLevel
   * @return {string} extracted business level name
   */
  businessLevelName(businessLevel: LevelItem): string {
    return businessLevel.name;
  }

  /**
   * Check if the button is disabled
   * @return {boolean}
   */
  isUpdateButtonDisabled(): boolean {
    return this.formGroup.invalid;
  }

  /**
   * Defines next season date in string format based on today
   * @return {string}
   */
  private getNextSeasonDate(): string {
    const today = new Date();
    let startingDateSeason2 = new Date(
      today.getFullYear(),
      this.JULY,
      this.FIRST
    );

    if (today > startingDateSeason2) {
      startingDateSeason2 = new Date(
        today.getFullYear() + 1,
        this.JANUARY,
        this.FIRST
      );
    }

    return startingDateSeason2.toISOString();
  }
}
